import { Link, useParams } from "react-router-dom";
import ProductCard from "@/components/feature/ProductCard";
import ProjectCard from "@/components/feature/ProjectCard";
import Reveal from "@/components/feature/Reveal";
import { projects } from "@/mocks/projects";
import { products } from "@/mocks/products";

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const project = projects.find((item) => item.id === id);

  if (!project) {
    return (
      <section className="flex min-h-[70vh] items-center justify-center bg-background-50 px-4 pt-24">
        <div className="text-center">
          <h1 className="font-heading text-3xl text-foreground-950">프로젝트를 찾을 수 없습니다</h1>
          <p className="mt-3 text-sm text-foreground-600">
            요청하신 시공 사례가 삭제되었거나 주소가 잘못되었을 수 있습니다.
          </p>
          <Link
            to="/projects"
            className="mt-7 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" /> 프로젝트 목록으로
          </Link>
        </div>
      </section>
    );
  }

  const usedProducts = products.filter((product) => project.materials.includes(product.name));
  const related = projects.filter((item) => item.category === project.category && item.id !== project.id).slice(0, 3);
  const fallbackRelated =
    related.length > 0 ? related : projects.filter((item) => item.id !== project.id).slice(0, 3);

  const info = [
    { label: "위치", value: project.location, icon: "ri-map-pin-line" },
    { label: "시공 연도", value: project.year, icon: "ri-calendar-line" },
    { label: "면적", value: project.area, icon: "ri-expand-diagonal-line" },
    { label: "고객", value: project.client, icon: "ri-user-line" },
  ];

  return (
    <>
      <section className="relative flex h-[440px] w-full items-end overflow-hidden md:h-[560px]">
        <img
          src={project.cover}
          alt={`${project.title} 시공 사례`}
          title={`${project.title} 동우스톤 시공 프로젝트`}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/35" />
        <div className="relative z-10 mx-auto w-full max-w-content px-4 pb-10 md:px-6 md:pb-14">
          <nav aria-label="현재 위치" className="flex flex-wrap items-center gap-2 text-xs text-background-200/80">
            <Link to="/" className="cursor-pointer transition-colors hover:text-background-50">
              홈
            </Link>
            <span>/</span>
            <Link to="/projects" className="cursor-pointer transition-colors hover:text-background-50">
              프로젝트
            </Link>
            <span>/</span>
            <span className="text-background-50">{project.title}</span>
          </nav>
          <span className="mt-5 inline-flex rounded-full bg-background-50/90 px-3.5 py-1.5 font-label text-xs font-medium text-foreground-800 backdrop-blur-sm">
            {project.category}
          </span>
          <h1 className="mt-4 font-heading text-3xl text-background-50 md:text-5xl">{project.title}</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-100/85 md:text-base">
            {project.summary}
          </p>
        </div>
      </section>

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto grid max-w-content grid-cols-2 gap-6 px-4 py-8 md:grid-cols-4 md:px-6 md:py-10">
          {info.map((item) => (
            <div key={item.label} className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                <i className={`${item.icon} text-lg`} />
              </span>
              <div>
                <p className="text-xs text-foreground-500">{item.label}</p>
                <p className="mt-0.5 text-sm font-medium text-foreground-900">{item.value}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-14">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">프로젝트 개요</h2>
              <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">{project.description}</p>
              <div className="mt-8 h-[280px] w-full overflow-hidden rounded-lg md:h-[420px]">
                <img
                  src={project.cover}
                  alt={`${project.title} 시공 상세`}
                  title={`${project.title} 동우스톤 시공 디테일`}
                  className="h-full w-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </div>
            <aside>
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h3 className="font-heading text-lg text-foreground-950">적용 소재</h3>
                <ul className="mt-4 space-y-3">
                  {project.materials.map((material) => (
                    <li key={material} className="flex items-center gap-3 text-sm text-foreground-700">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                        <i className="ri-check-line text-sm" />
                      </span>
                      {material}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 border-t border-background-200 pt-6">
                  <p className="text-xs leading-relaxed text-foreground-600">
                    이 공간과 비슷한 시공을 원하시면 소재 상담을 통해 예산과 일정을 함께 정리해 드립니다.
                  </p>
                  <Link
                    to="/contact"
                    className="mt-4 flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-customer-service-2-line" /> 상담 문의하기
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {usedProducts.length > 0 && (
        <section className="bg-background-100">
          <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">이 공간에 사용된 소재</h2>
            <div data-product-shop className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {usedProducts.map((product, index) => (
                <Reveal key={product.id} delay={index * 70}>
                  <ProductCard product={product} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-background-50">
        <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">비슷한 시공 사례</h2>
            <Link
              to="/projects"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              전체 프로젝트 보기 <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            {fallbackRelated.map((item, index) => (
              <Reveal key={item.id} delay={index * 70}>
                <ProjectCard project={item} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}